import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { MdDownload, MdCenterFocusStrong } from 'react-icons/md';
import StaticImageViewer, { StaticViewportState } from 'components/ImageViewer/StaticImageViewer';
import PanoramicImageViewer from 'components/ImageViewer/PanoramicImageViewer';
import { useImages, isBlobImage } from 'customHooks/useImages';
import type { LayerFeature, PanoramaState, SyncMode } from 'components/ImageViewer/types';
import type { PlaybackLayer } from './types';
import {
  getFeatureSyncValue,
  hasSpatialFocusValue,
  NO_SPATIAL_INDEX_MESSAGE,
} from 'utils/syncIndex';

export interface PlaybackViewerDownloadState {
  layerKey: string;
  downloadSrc: string | null;
  fileName: string;
  canDownload: boolean;
}

interface Props {
  layer: PlaybackLayer;
  syncMode: SyncMode;
  targetSyncValue: number | null;
  showFocusButton: boolean;
  isFocusOwner: boolean;
  onSetSyncModeFromFeature: (
    layerKey: string,
    feature: LayerFeature,
    mode: SyncMode,
  ) => void;
  onPanoramaStateChange: (layerKey: string, state: PanoramaState | null) => void;
  onDownloadStateChange: (state: PlaybackViewerDownloadState) => void;
  staticViewportState: StaticViewportState | null;
  onStaticViewportStateChange: (state: StaticViewportState) => void;
}

const PlaybackViewerCard: React.FC<Props> = ({
  layer,
  syncMode,
  targetSyncValue,
  showFocusButton,
  isFocusOwner,
  onSetSyncModeFromFeature,
  onPanoramaStateChange,
  onDownloadStateChange,
  staticViewportState,
  onStaticViewportStateChange,
}) => {
  const [panoramaState, setPanoramaState] = useState<PanoramaState | null>(null);
  const [hasError, setHasError] = useState(false);
  const [stableSrc, setStableSrc] = useState<string | null>(null);
  const [panoReadyForPath, setPanoReadyForPath] = useState<string | null>(null);

  const feature = useMemo(() => {
    if (targetSyncValue === null) return null;
    return (
      layer.features.find(
        (candidate) => getFeatureSyncValue(candidate, syncMode) === targetSyncValue,
      ) ?? null
    );
  }, [layer.features, syncMode, targetSyncValue]);

  const featureIndex = useMemo(() => {
    if (!feature) return -1;
    return layer.features.findIndex((candidate) => candidate === feature);
  }, [feature, layer.features]);

  const hasMatchingImage = Boolean(feature);
  const currentFilePath = feature?.filePath ?? '';
  const imageType = feature?.imageType ?? 'STATIC';

  const preloadUrls = useMemo(() => {
    const urls: string[] = [];
    const seen = new Set<string>();
    const push = (i: number) => {
      if (i < 0 || i >= layer.features.length) return;
      const fp = layer.features[i]?.filePath;
      if (fp && !seen.has(fp)) { seen.add(fp); urls.push(fp); }
    };
    if (featureIndex < 0) return urls;
    push(featureIndex);
    push(featureIndex + 1);
    push(featureIndex - 1);
    push(featureIndex + 2);
    push(featureIndex - 2);
    return urls;
  }, [featureIndex, layer.features]);

  const { images, isLoading: isBlobLoading } = useImages(preloadUrls);

  const activeResult = currentFilePath
    ? images.find((img) => img.url === currentFilePath)
    : undefined;
  const activeBlobImage = activeResult && isBlobImage(activeResult) ? activeResult : null;
  const activeImageSrc = activeBlobImage?.dataUri ?? null;
  const isPending = hasMatchingImage && !!currentFilePath && isBlobLoading && !activeBlobImage;
  const downloadFileName = useMemo(() => {
    const fallbackExt = '.jpg';
    const sourcePath = currentFilePath || layer.features[featureIndex]?.filePath || '';
    const withoutQuery = sourcePath.split('?')[0];
    const candidate = withoutQuery.split('/').pop() || 'image';
    const decoded = decodeURIComponent(candidate);
    return /\.[a-zA-Z0-9]+$/.test(decoded) ? decoded : `${decoded}${fallbackExt}`;
  }, [currentFilePath, featureIndex, layer.features]);
  const activeBlobError =
    !isBlobLoading && activeResult && !isBlobImage(activeResult)
      ? activeResult.error
      : null;

  // Reset error state when the image path changes
  const prevFilePathRef = useRef(currentFilePath);
  useEffect(() => {
    if (prevFilePathRef.current !== currentFilePath) {
      prevFilePathRef.current = currentFilePath;
      setHasError(false);
    }
  }, [currentFilePath]);

  // Keep last successful source while next image fetch is in-flight to avoid flash/flicker.
  useEffect(() => {
    if (activeImageSrc) {
      setStableSrc(activeImageSrc);
      setHasError(false);
    }
  }, [activeImageSrc]);

  useEffect(() => {
    if (activeBlobError) {
      setHasError(true);
    }
  }, [activeBlobError]);

  const currentFilePathRef = useRef(currentFilePath);
  currentFilePathRef.current = currentFilePath;

  const handleLoad = useCallback(() => {
    setHasError(false);
    setPanoReadyForPath(currentFilePathRef.current);
  }, []);
  const handleError = useCallback(() => setHasError(true), []);

  const shouldFlip = /\b(back|rear)\b/i.test(layer.layerName ?? '');
  const featureHasSpatialIndex = hasSpatialFocusValue(feature);
  const canShowFocusToggle = showFocusButton;
  const isSpatialSyncMode = syncMode === 'spatial';
  const focusToggleDisabled = !isSpatialSyncMode && !featureHasSpatialIndex;
  const focusTooltip = focusToggleDisabled
    ? NO_SPATIAL_INDEX_MESSAGE
    : isSpatialSyncMode
      ? 'Remove focus'
      : 'Set focus';

  const handlePanoramaPosition = useCallback((yaw: number, pitch: number) => {
    setPanoramaState((prev) => {
      const next = {
        yaw,
        pitch,
        zoom: prev?.zoom ?? 50,
      };
      onPanoramaStateChange(layer.layerKey, next);
      return next;
    });
  }, [layer.layerKey, onPanoramaStateChange]);

  const handlePanoramaZoom = useCallback((zoom: number) => {
    setPanoramaState((prev) => {
      const next = {
        yaw: prev?.yaw ?? 0,
        pitch: prev?.pitch ?? 0,
        zoom,
      };
      onPanoramaStateChange(layer.layerKey, next);
      return next;
    });
  }, [layer.layerKey, onPanoramaStateChange]);

  useEffect(() => {
    if (imageType !== 'PANO') {
      onPanoramaStateChange(layer.layerKey, null);
    }
  }, [imageType, layer.layerKey, onPanoramaStateChange]);

  const handleToggleFocusMode = useCallback(() => {
    if (!feature) return;
    onSetSyncModeFromFeature(layer.layerKey, feature, isSpatialSyncMode ? 'image' : 'spatial');
  }, [feature, isSpatialSyncMode, layer.layerKey, onSetSyncModeFromFeature]);

  const handleDownload = useCallback(() => {
    if (!activeImageSrc) return;

    const anchor = document.createElement('a');
    anchor.href = activeImageSrc;
    anchor.download = downloadFileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [activeImageSrc, downloadFileName]);

  const displaySrc = stableSrc || activeImageSrc;
  const isPanoReady = panoReadyForPath === currentFilePath;
  const showLoader = isPending || (imageType === 'PANO' && !isPanoReady && !hasError && hasMatchingImage);
  const showNoImageAvailable = !hasMatchingImage;
  const showError = !showNoImageAvailable && hasError;

  useEffect(() => {
    onDownloadStateChange({
      layerKey: layer.layerKey,
      downloadSrc: activeImageSrc,
      fileName: downloadFileName,
      canDownload: Boolean(activeImageSrc) && !showError && !isPending,
    });
  }, [
    activeImageSrc,
    downloadFileName,
    isPending,
    layer.layerKey,
    onDownloadStateChange,
    showError,
  ]);

  useEffect(() => () => {
    onDownloadStateChange({
      layerKey: layer.layerKey,
      downloadSrc: null,
      fileName: 'image.jpg',
      canDownload: false,
    });
  }, [layer.layerKey, onDownloadStateChange]);

  return (
    <div className="playback-viewer-card">
      <div className="modal-header playback-viewer-header">
        <div className="modal-title-container">
          <div className="modal-title-in-container playback-viewer-layer-name" title={layer.layerName}>
            {layer.layerName}
          </div>
        </div>
        <div className="modal-header-buttons">
          <button
            type="button"
            className="modal-control image-viewer-download-btn"
            onClick={handleDownload}
            title={activeImageSrc ? 'Download image' : 'Image is not ready to download'}
            aria-label="Download image"
            disabled={!activeImageSrc || showError || isPending}
          >
            <MdDownload />
          </button>
          {canShowFocusToggle && (
            <button
              type="button"
              className={`modal-control image-viewer-focus-btn${isSpatialSyncMode ? ' image-viewer-focus-btn--active' : ''}${isFocusOwner && isSpatialSyncMode ? ' image-viewer-focus-btn--owner' : ''}`}
              onClick={handleToggleFocusMode}
              title={focusTooltip}
              aria-label={focusTooltip}
              disabled={focusToggleDisabled}
            >
              <MdCenterFocusStrong />
            </button>
          )}
        </div>
      </div>
      <div className="modal-content image-viewer-content playback-viewer-body">
        {showLoader && (
          <div className="playback-viewer-loading">
            <div className="xa-loader-content">
              <svg
                className="xa-loader"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          </div>
        )}
        {showNoImageAvailable && (
          <div className="playback-viewer-error">No image available</div>
        )}
        {showError && (
          <div className="playback-viewer-error">Image not available</div>
        )}
        {!showNoImageAvailable && !showError && !!displaySrc && imageType === 'STATIC' && (
          <StaticImageViewer
            src={displaySrc}
            onLoad={handleLoad}
            onError={handleError}
            shouldFlipHorizontally={shouldFlip}
            viewportState={staticViewportState}
            onViewportStateChange={onStaticViewportStateChange}
          />
        )}
        {!showNoImageAvailable && !showError && !!displaySrc && imageType === 'PANO' && (
          <PanoramicImageViewer
            src={displaySrc}
            panoramaState={panoramaState}
            onPositionChange={handlePanoramaPosition}
            onZoomChange={handlePanoramaZoom}
            onReady={handleLoad}
            onError={handleError}
          />
        )}
      </div>
    </div>
  );
};

export default PlaybackViewerCard;
